import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Wallet } from 'lucide-react';
import { api } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useSelfIsStructural } from '@/hooks/useSelfCreditPool';
import { signedMoney } from '@/pages/settlements/signed-money';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface CreditBalance {
  balance: string;
  suspended: boolean;
  currency: string;
  /** Absent on a backend older than the field — see the read in the component. */
  hasBook?: boolean;
}

/** Minor units, tolerant of a malformed body: an unparseable balance must not read
 * as "in credit", so it falls back to zero (which the caller treats as blocked). */
function minorUnits(balance: string): bigint {
  try {
    return BigInt(balance.trim());
  } catch {
    return 0n;
  }
}

/** The `tenancyNodeId` claim of the active access token — the node THIS BO session is
 * (the company root for a normal admin, or the impersonated senior/master/agent). */
function selfNodeId(token: string | null): string | null {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as {
      tenancyNodeId?: string | null;
    };
    return claims.tenancyNodeId ?? null;
  } catch {
    return null;
  }
}

/**
 * The logged-in account's prepaid-credit balance, shown in the top navbar.
 * Hidden for structural/root nodes (no credit book — the backend errors), so a
 * normal company admin sees nothing; an impersonated senior/master/agent sees theirs.
 */
export function NavbarCredit() {
  const { t } = useTranslation();
  const token = useAuthStore((s) => s.accessToken);
  const nodeId = selfNodeId(token);

  // A structural node holds no credit book, and asking anyway is a 400 from a
  // money endpoint on every company admin's first paint. `undefined` is "not
  // known yet" — wait rather than guess in either direction.
  const structural = useSelfIsStructural();

  const q = useQuery<CreditBalance>({
    queryKey: ['/api/bo/mlm-credit', nodeId],
    queryFn: () => api.get<CreditBalance>(`/api/bo/mlm-credit/${nodeId}`),
    enabled: !!nodeId && structural === false,
    retry: false, // still no retry: an error here is not something a repeat fixes
    // The gate above removes the known-refusal case; this keeps the older
    // belt-and-braces rule for anything else that errors, so one bad response
    // cannot turn into a 30s poll of the same bad response.
    refetchInterval: (query) => (query.state.error ? false : 30_000),
    refetchOnWindowFocus: (query) => !query.state.error,
  });

  if (!nodeId || q.isError || !q.data) return null;

  const { hasBook, suspended, balance, currency } = q.data;
  // Strictly `=== false`: this SPA deploys independently of svc-commission, so a
  // response predating the field must keep showing money rather than turn every
  // funded agent's chip into an em dash.
  const noBook = hasBook === false;
  const sm = noBook ? null : signedMoney(balance, currency);
  // This endpoint reports the raw `suspendedAt` flag, while /summary — which feeds the
  // dashboard card on the same screen — reports the real play gate (balance <= 0 OR
  // suspendedAt). Deriving the gate here keeps a node spent to exactly zero from
  // reading neutral in the navbar and red on the card.
  const outOfCredit = !noBook && (suspended || minorUnits(balance) <= 0n);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium tabular-nums',
            noBook ? 'text-muted-foreground' : outOfCredit ? 'text-destructive' : 'text-foreground',
          )}
        >
          <Wallet className="h-4 w-4 opacity-70" />
          {sm ? sm.text : '—'}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        {noBook ? (
          t('mlm.credit.noBook')
        ) : (
          <>
            {t('mlm.credit.balance')}
            {outOfCredit ? ` · ${t('mlm.credit.suspended')}` : ''}
          </>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
